import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { API_URL } from "@/config";

const TicketContext = createContext();

export const useTickets = () => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error('useTickets must be used within a TicketProvider');
  }
  return context;
};

export const TicketProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tickets`);
      if (!res.ok) return;
      const data = await res.json();
      setTickets(data.active);
      setClosedTickets(data.closed);
    } catch (err) {
      // Silently retry on next cycle
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 5000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  const addTicket = async (ticket) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticket)
      });
      const newTicket = await res.json();
      setTickets(prev => [...prev, newTicket]);
    } catch (err) {
      console.error('Failed to create ticket:', err);
    }
  };

  const updateTicket = async (ticketId, updatedData) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData)
      });
      const updated = await res.json();
      setTickets(prev => prev.map(t => t.ticketId === ticketId ? updated : t));
      return updated;
    } catch (err) {
      console.error('Failed to update ticket:', err);
      return null;
    }
  };

  const closeTicket = async (ticketId, closingComments) => {
    try {
      const res = await fetch(`${API_URL}/api/tickets/${ticketId}/close`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ closingComments })
      });
      const closedTicket = await res.json();
      setClosedTickets(prev => [...prev, closedTicket]);
      setTickets(prev => prev.filter(t => t.ticketId !== ticketId));
    } catch (err) {
      console.error('Failed to close ticket:', err);
    }
  };

  const getTicketsForSubscriber = (subscriberId) => {
    return tickets.filter(t => t.subscriberId === subscriberId && t.status !== 'Closed');
  };

  const getClosedTicketsForSubscriber = (subscriberId) => {
    return closedTickets.filter(t => t.subscriberId === subscriberId);
  };

  const getDispatchedTickets = () => {
    return tickets.filter(t => ['Dispatched', 'Dispatch Acknowledged', 'En Route', 'On Site', 'Work Complete'].includes(t.status));
  };

  const getTicketedDeviceIds = () => {
    return tickets.map(t => t.deviceId);
  };

  return (
    <TicketContext.Provider value={{ 
      tickets, 
      closedTickets,
      addTicket, 
      updateTicket, 
      closeTicket,
      getTicketsForSubscriber, 
      getClosedTicketsForSubscriber,
      getDispatchedTickets,
      getTicketedDeviceIds 
    }}>
      {children}
    </TicketContext.Provider>
  );
};
